import { forwardRef } from 'react'
import { CAP_ROOT_CLASS } from '@/features/client-app-preview/theme/cap-tokens'
import { cn } from '@/shared/lib/utils'
import '@/features/client-app-preview/theme/cap-fonts.css'
import '@/features/client-app-preview/theme/cap-tokens.css'
import '@/features/client-app-preview/theme/cap-preview.css'

/**
 * EL LIENZO DEL CARTAPACIO: el único nodo del back-office que enciende los tokens `--cap-`.
 *
 * ## Por qué el cartapacio se dibuja con la UI de la app
 *
 * Porque **ya encajaba**. La app del cliente resuelve exactamente este problema —carpetas,
 * tarjetas con miniatura, la pila de hojas que distingue un documento de varias páginas de uno de
 * una sola, el visor— y lo resuelve para el MISMO archivo. Rehacerlo en vocabulario de
 * back-office daba dos dibujos distintos del mismo documento y, peor, dejaba al curador
 * decidiendo qué publicar sin ver cómo se va a ver. Acá lo ve, y encima le agregamos los
 * controles: publicar, renombrar, reclasificar, descargar, eliminar.
 *
 * ## ⚠️ Esto contradice una línea de `apps/web-real-estate/CLAUDE.md`
 *
 * Esa guía dice «nunca importes un `Cap*` fuera de `client-app-preview/`». La regla se escribió
 * para proteger la FIDELIDAD del preview —que el marco no termine pareciéndose al back-office— y
 * acá el movimiento es el inverso: el back-office toma prestado el lenguaje de la app, y el
 * preview no se entera. Las dos reglas que sí sostienen esa protección se respetan al pie:
 *
 * 1. **Ningún `cap-*` se modifica.** Se componen y se envuelven. Si hace falta una variante, va
 *    por prop opcional sin tocar el default (ADR 0039).
 * 2. **El chrome de edición NO se dibuja con `Cap*`.** Los menús, chips y diálogos son de
 *    `shared/ui`: son del back-office y tienen que parecerlo. Lo que se toma prestado es cómo se
 *    ve un DOCUMENTO, no cómo se ve un botón de administración.
 *
 * Queda anotado para que la guía se actualice, no resuelto en silencio.
 *
 * ## Qué hace este nodo, concretamente
 *
 * Las tres hojas del preview cuelgan de `CAP_ROOT_CLASS`, así que sin esta clase los `Cap*`
 * renderizan sin una sola variable resuelta: texto negro del navegador sobre fondo transparente.
 * Acá se enciende, y —igual que en el marco— **sólo acá**: nada de lo que la app define puede
 * escaparse al resto de la plataforma.
 *
 * `isolation: isolate` no es adorno. Las hojas apiladas de una tarjeta se posicionan con un
 * `z-index` por hoja; sin un contexto de apilamiento propio esos índices suben hasta el del panel
 * y compiten con el encabezado pegajoso y con los overlays. Es el mismo escape que ya le pasó al
 * indicador de inicio dentro del marco del teléfono.
 *
 * `@container` habilita las consultas de contenedor de todo lo que cuelgue: el panel se arrastra
 * entre 320 y 600 px sin que el viewport cambie, así que una media query no ve nada. La grilla
 * pasa de dos a tres columnas mirando ESTE ancho, que es el único que se mueve.
 */
export const PortfolioCanvas = forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  function PortfolioCanvas({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        data-slot="portfolio-canvas"
        className={cn(CAP_ROOT_CLASS, '@container isolate w-full', className)}
        style={{ isolation: 'isolate' }}
        {...props}
      />
    )
  }
)
